/**
 * @voltxampere/engine — DC analysis orchestrator
 *
 * Sprint 5's headline class. The solver wires the planner, MNA
 * builder, Newton-Raphson loop, and the §3.6 / §3.7 fallbacks into
 * a single async entry point that takes a {@link Circuit} and
 * returns a {@link DCResult}. Async even though the work is
 * synchronous today — Sprint 6+ will offload to a Worker, and
 * keeping the surface async now means the migration is a one-line
 * implementation swap rather than a public-API break.
 *
 * Solve attempt order (SPECS §3.6, §3.7):
 *   1. Plain Newton-Raphson from zeros.
 *   2. Gmin stepping if (1) fails to converge.
 *   3. Source stepping if (2) fails.
 *   4. Otherwise: throw `ConvergenceError`.
 *
 * The result captures node voltages by net id, voltage-source
 * currents by component id, the iteration count actually consumed,
 * and which fallback (if any) succeeded — useful for the UI's
 * "your simulation converged using gmin stepping" hint.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ConvergenceError } from "@voltxampere/shared";
import { planCircuit } from "./circuit-plan.js";
import { newtonRaphson } from "./newton-raphson.js";
import { newtonRaphsonAnderson } from "./anderson-newton.js";
import { gminStepping } from "./gmin-stepping.js";
import { sourceStepping } from "./source-stepping.js";
import { applyInitialConditions } from "./initial-conditions.js";
import { areDCStampsRegistered } from "../stamps/register.js";
import { runAssertionsOnDC } from "../assertion/run.js";
export class DCSolver {
    // The body is currently synchronous, but the public surface is
    // `Promise<DCResult>` so Sprint 6+'s Worker offload can land
    // without breaking callers. We use `eslint-disable` rather than a
    // dummy `await Promise.resolve()` because the latter would add a
    // microtask hop that current callers don't pay for.
    // eslint-disable-next-line @typescript-eslint/require-await
    async solve(circuit, options = {}) {
        if (!areDCStampsRegistered()) {
            throw new ConvergenceError("DCSolver.solve called before registerDCStamps() — no stamp dispatch table");
        }
        options.signal?.throwIfAborted();
        options.onProgress?.(0, "init");
        const plan = planCircuit(circuit);
        const settings = circuit.simulationSettings.dc;
        // Sprint 8c: resolve user-supplied initial conditions into a
        // seeded x0 and pnjlim junction history. When no IC is given
        // we fall back to the pre-8c cold-start (zeros, empty map).
        const xCold = new Float64Array(plan.size);
        const ic = applyInitialConditions(circuit, plan, settings.initialConditions, xCold);
        const hasInitialConditions = settings.initialConditions !== undefined && ic.applied > 0;
        const strictOpts = {
            maxIterations: settings.maxIterations,
            reltol: settings.reltol,
            abstol: settings.abstol,
            // Sprint 8a: schema flag, default true. Passing `false` from
            // a test fixture reproduces pre-8a Newton behaviour — useful
            // for proving the damping is what rescued a given circuit.
            enableDeltaVDamping: settings.enableDeltaVDamping,
            // Sprint 8c: Armijo gate. Threaded through the ladder so
            // gmin / source / Anderson continuations inherit the flag.
            enableArmijo: settings.enableArmijo,
            // Sprint 80.Z L-AYARLAR-5 — operating temperature.
            //
            // `circuit.simulationSettings.dc.temperature` is in **Celsius**
            // (SPICE-classic `.OPTIONS TEMP=27` convention; SPECS §3.5
            // default). We convert to Kelvin at the solver boundary so
            // the stamp context, delta-V trust region, and physical
            // constants all consume the SI unit (T_K = T_C + 273.15).
            //
            // AYARLAR > Sıcaklık in the web UI flows into this field via
            // `apps/web/src/store/engine-worker-client.ts` — the web
            // sanitizer writes Celsius too so the schema convention is
            // honoured end-to-end.
            temperature: settings.temperature + 273.15,
            tier: "strict",
        };
        const relaxedOpts = { ...strictOpts, tier: "relaxed" };
        // Newton/gmin/source already have broader tolerances on paper;
        // the tier just controls the reduced-KCL convergence predicate.
        const twoTier = settings.toleranceStrategy === "two-tier";
        // Attempt 1: plain Newton (strict tier first). Seeded with the
        // initial-conditions result when provided; falls back to zeros
        // otherwise.
        let directIterations = 0;
        let directClampEvents = 0;
        let directArmijoEvents = 0;
        let directLastX = null;
        try {
            const direct = newtonRaphson(circuit, plan, {
                ...strictOpts,
                x0: ic.x,
                initialJunctionHistory: ic.junctionHistory,
                hasInitialConditions,
            });
            directIterations = direct.iterations;
            directClampEvents = direct.clampEvents;
            directArmijoEvents = direct.armijoEvents;
            directLastX = direct.x;
            if (direct.converged) {
                return this.buildResult(circuit, plan, direct.x, direct.iterations, "none", direct.clampEvents, direct.armijoEvents, "strict", direct.finalResidualNorm, ic);
            }
        }
        catch (e) {
            if (!(e instanceof ConvergenceError))
                throw e;
            directIterations = this.iterationsFromMeta(e);
        }
        // Sprint 8b: two-tier retry. If strict Newton stopped on an
        // iterate that DID satisfy the relaxed band, accept it before
        // paying for gmin stepping. The relaxed tier signals a 10×-loose
        // convergence that DC consumers can opt into per
        // `SimulationSettings.dc.toleranceStrategy`.
        if (twoTier && directLastX !== null) {
            try {
                const relaxed = newtonRaphson(circuit, plan, {
                    ...relaxedOpts,
                    x0: directLastX,
                    // Warm-start pnjlim from the strict pass's seed (still
                    // valid after a failed strict run — the caller's IC
                    // hints remain the user's best guess).
                    initialJunctionHistory: ic.junctionHistory,
                    hasInitialConditions,
                    maxIterations: Math.max(10, settings.maxIterations / 4),
                });
                directIterations += relaxed.iterations;
                directClampEvents += relaxed.clampEvents;
                directArmijoEvents += relaxed.armijoEvents;
                if (relaxed.converged) {
                    return this.buildResult(circuit, plan, relaxed.x, directIterations, "none", directClampEvents, directArmijoEvents, "relaxed", relaxed.finalResidualNorm, ic);
                }
            }
            catch (e) {
                if (!(e instanceof ConvergenceError))
                    throw e;
                directIterations += this.iterationsFromMeta(e);
            }
        }
        options.signal?.throwIfAborted();
        options.onProgress?.(25, "gmin-stepping");
        // Attempt 2: gmin stepping. Catch ConvergenceError so we can
        // try source stepping next.
        let gminIterations = directIterations;
        let gminClampEvents = directClampEvents;
        let gminArmijoEvents = directArmijoEvents;
        try {
            const gmin = gminStepping(circuit, plan, strictOpts);
            gminIterations += gmin.totalIterations;
            gminClampEvents += gmin.totalClampEvents;
            gminArmijoEvents += gmin.totalArmijoEvents;
            return this.buildResult(circuit, plan, gmin.result.x, gminIterations, "gmin", gminClampEvents, gminArmijoEvents, "strict", gmin.result.finalResidualNorm, ic);
        }
        catch (e) {
            if (!(e instanceof ConvergenceError))
                throw e;
            gminIterations += this.iterationsFromMeta(e);
        }
        options.signal?.throwIfAborted();
        options.onProgress?.(50, "source-stepping");
        // Attempt 3: source stepping.
        let totalIterations = gminIterations;
        let totalClampEvents = gminClampEvents;
        let totalArmijoEvents = gminArmijoEvents;
        let sourceLastX = null;
        try {
            const src = sourceStepping(circuit, plan, strictOpts);
            totalIterations += src.totalIterations;
            totalClampEvents += src.totalClampEvents;
            totalArmijoEvents += src.totalArmijoEvents;
            sourceLastX = src.result.x;
            return this.buildResult(circuit, plan, src.result.x, totalIterations, "source", totalClampEvents, totalArmijoEvents, "strict", src.result.finalResidualNorm, ic);
        }
        catch (e) {
            if (!(e instanceof ConvergenceError))
                throw e;
            totalIterations += this.iterationsFromMeta(e);
        }
        // Sprint 8b: two-tier retry on source stepping's last iterate.
        // Source stepping can land at a mostly-correct OP that strict
        // rejects but relaxed accepts — particularly for PNP CE fixtures.
        if (twoTier) {
            const seed = sourceLastX ?? directLastX ?? new Float64Array(plan.size);
            try {
                const relaxed = newtonRaphson(circuit, plan, {
                    ...relaxedOpts,
                    x0: seed,
                    // Warm-start from the original IC hints even at this
                    // stage — pnjlim's continuity across the gmin / source
                    // continuations is broken by design (each gets a fresh
                    // history), so re-seeding from the user's hint is the
                    // only meaningful warm-start left.
                    initialJunctionHistory: ic.junctionHistory,
                    hasInitialConditions,
                    maxIterations: settings.maxIterations,
                });
                totalIterations += relaxed.iterations;
                totalClampEvents += relaxed.clampEvents;
                totalArmijoEvents += relaxed.armijoEvents;
                if (relaxed.converged) {
                    return this.buildResult(circuit, plan, relaxed.x, totalIterations, "source", totalClampEvents, totalArmijoEvents, "relaxed", relaxed.finalResidualNorm, ic);
                }
            }
            catch (e) {
                if (!(e instanceof ConvergenceError))
                    throw e;
                totalIterations += this.iterationsFromMeta(e);
            }
        }
        // Sprint 8b: Anderson-II opt-in fallback. Last resort for
        // limit-cycle topologies (PNP CE saturation edge) that defeat
        // plain Newton + gmin + source stepping. Off by default until
        // Sprint 9 observational validation; turn on via
        // `SimulationSettings.dc.enableAnderson = true`.
        //
        // Seed from cold start (zeros): warm-starting from the direct
        // Newton or source-stepping fail state risks feeding Anderson
        // an LU-singular iterate whose admittance matrix has a zero
        // pivot. Cold start lets pnjlim's Nagel climb bring the junction
        // voltages up the same way a fresh Newton would.
        if (settings.enableAnderson) {
            options.signal?.throwIfAborted();
            options.onProgress?.(75, "anderson");
            const andersonTiers = twoTier ? ["strict", "relaxed"] : ["strict"];
            for (const andersonTier of andersonTiers) {
                try {
                    const andersonOpts = andersonTier === "strict" ? strictOpts : relaxedOpts;
                    const anderson = newtonRaphsonAnderson(circuit, plan, {
                        ...andersonOpts,
                        x0: new Float64Array(plan.size),
                    });
                    totalIterations += anderson.iterations;
                    totalClampEvents += anderson.clampEvents;
                    totalArmijoEvents += anderson.armijoEvents;
                    if (anderson.converged) {
                        return this.buildResult(circuit, plan, anderson.x, totalIterations, "anderson", totalClampEvents, totalArmijoEvents, andersonTier, anderson.finalResidualNorm, ic);
                    }
                }
                catch (e) {
                    if (e instanceof ConvergenceError) {
                        // Sprint 8b.1 P0-3 (Agent 2): the Sprint 8b code had a
                        // separate `if (e instanceof SingularMatrixError)` arm
                        // here that `continue`d without `iterationsFromMeta`,
                        // dropping iterations done before a zero-pivot. The
                        // anderson-newton wrapper already converts LU
                        // singularity into ConvergenceError with
                        // `meta.iterations`, so any leftover SingularMatrixError
                        // would indicate an unexpected leak — let it propagate
                        // rather than silently swallow.
                        totalIterations += this.iterationsFromMeta(e);
                        continue;
                    }
                    throw e;
                }
            }
        }
        throw new ConvergenceError(`DC solve failed: plain Newton, gmin stepping, and source stepping all diverged${twoTier ? " (both strict and relaxed tiers)" : ""}`, {
            meta: {
                iterations: totalIterations,
                tried: twoTier ? ["newton", "gmin", "source", "relaxed"] : ["newton", "gmin", "source"],
            },
        });
    }
    iterationsFromMeta(e) {
        const meta = e.meta;
        if (meta === undefined)
            return 0;
        const v = meta.iterations;
        return typeof v === "number" && Number.isFinite(v) ? v : 0;
    }
    buildResult(circuit, plan, x, iterations, usedFallback, clampEvents, armijoEvents, convergenceTier, finalResidualNorm, 
    /**
     * Sprint 10.0 (smell #3 closure): initial-conditions ignored
     * lists. Empty when no IC hints were supplied; non-empty when
     * a typo'd net or component id slipped through. Surface as
     * `DCWarning` entries so the caller learns about it instead of
     * silently using the unprocessed default.
     */
    ic) {
        const nodeVoltages = new Map();
        nodeVoltages.set(plan.groundNetId, 0);
        for (const [netId, idx] of plan.netToIndex.entries()) {
            nodeVoltages.set(netId, x[idx] ?? 0);
        }
        const sourceCurrents = new Map();
        for (const [componentId, idx] of plan.vSourceIdToIndex.entries()) {
            // Sprint 24 B1 — vsource rows live AFTER the internal-node block.
            sourceCurrents.set(componentId, x[plan.nodeCount + plan.internalNodeCount + idx] ?? 0);
        }
        // Sprint 8b.1 (Agent 3 P0-2): trivial-fixed-point detection.
        // The reduced-KCL residual converges identically when every
        // active device sits in cut-off (BJT off, diode reverse-biased)
        // because all currents are zero and KCL is trivially satisfied.
        // For circuits with at least one BJT/diode in the netlist, that
        // is almost certainly NOT the operating point the user intended.
        // Surface a warning so consumers can decide whether to retry
        // with a different x0 / .nodeset hint / pseudo-transient seed.
        const warnings = [];
        // Sprint 10.0 (smell #3 closure): IC typo warnings precede the
        // trivial-FP heuristic so consumers see the actionable advice
        // ("you misspelled net X") before the secondary inference. Each
        // ignored id gets its own warning entry — a user with two typos
        // sees two warnings.
        for (const netId of ic.ignoredNets) {
            warnings.push({
                code: "ic-unknown-net",
                message: `Initial-condition node hint for '${netId}' was ignored — no such net exists in the circuit. Check the spelling against the circuit's nets array.`,
            });
        }
        for (const compId of ic.ignoredComponents) {
            warnings.push({
                code: "ic-unknown-component",
                message: `Initial-condition component hint for '${compId}' was ignored — no such component exists in the circuit. Check the spelling against the circuit's components array.`,
            });
        }
        const ACTIVE_TYPES = new Set(["bjt-npn", "bjt-pnp", "diode"]);
        const hasActive = circuit.components.some((c) => ACTIVE_TYPES.has(c.type));
        if (hasActive) {
            // Heuristic: largest absolute branch current across voltage
            // sources. If below picoamp (≪ realistic OP currents) AND a
            // BJT/diode exists, assume trivial fixed point.
            const TRIVIAL_CURRENT_THRESHOLD = 1e-9;
            let maxAbsCurrent = 0;
            for (const i of sourceCurrents.values()) {
                const a = Math.abs(i);
                if (a > maxAbsCurrent)
                    maxAbsCurrent = a;
            }
            if (maxAbsCurrent < TRIVIAL_CURRENT_THRESHOLD) {
                warnings.push({
                    code: "trivial-active-device-off",
                    message: `DC solve converged but every voltage-source branch carries < ${String(TRIVIAL_CURRENT_THRESHOLD)} A despite the circuit containing an active device. This is almost certainly the BJT-off / diode-off trivial fixed point, not the intended operating point. Consider supplying initial conditions or running a pseudo-transient warm-up.`,
                });
            }
        }
        // Sprint 9: evaluate any attached assertions against the
        // converged operating point. Parse/evaluation errors become
        // failed AssertionResult entries — they never bubble out of
        // the solver, so a malformed user DSL string cannot break
        // the analysis pipeline.
        const assertionResults = runAssertionsOnDC(circuit, {
            converged: true,
            iterations,
            nodeVoltages,
            sourceCurrents,
            usedFallback,
            clampEvents,
            armijoEvents,
            convergenceTier,
            finalResidualNorm,
            warnings,
            assertionResults: [], // placeholder — runAssertionsOnDC ignores this field
        });
        return {
            converged: true,
            iterations,
            nodeVoltages,
            sourceCurrents,
            usedFallback,
            clampEvents,
            armijoEvents,
            convergenceTier,
            finalResidualNorm,
            warnings,
            assertionResults,
        };
    }
}
//# sourceMappingURL=solver.js.map